import React from 'react';
import styled from 'styled-components';

import {
  Theme,
} from 'common';

import { Arrow } from 'common/components';

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
  
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const ContentWrapper = styled.div`
  display: inline-block;
  height: 100%;
  white-space: nowrap;
  position: relative;
`;

const ArrowContainer = styled.div`
  position: absolute;
  top: 45%;
  z-index: 10;
  width: 40px;
  height: 40px;
  background-color: ${Theme.Card.Background};
  box-shadow: 0 0 4px rgba(0, 0, 0, .5);
  border-radius: 50%;
  cursor: pointer;
  padding: ${props => props.padding || '0'};
  
  ${props => props.left && 'left: 10px;'}
  ${props => props.right && 'right: 10px;'}
`;

export const HorizontalScroll = ({ children }) => {
  const scrollRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const [position, setPosition] = React.useState(0);
  const [showArrows, setShowArrows] = React.useState(false);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStartRef = React.useRef({ x: 0, position: 0 });
  const touchDragRef = React.useRef({ x: -999, position: 0 });

  const checkScrollability = React.useCallback(() => {
    if (!scrollRef.current || !contentRef.current) return;

    const containerWidth = scrollRef.current.clientWidth;
    const contentWidth = contentRef.current.scrollWidth;
    const scrollMax = contentWidth - containerWidth;
    const minPosition = -scrollMax;

    const needsScroll = contentWidth > containerWidth;
    setShowArrows(needsScroll);
    setCanScrollLeft(needsScroll && position < 0);
    setCanScrollRight(needsScroll && position > minPosition);
  }, [position]);

  const getMaxPosition = React.useCallback(() => {
    if (!scrollRef.current || !contentRef.current) return 0;
    const containerWidth = scrollRef.current.clientWidth;
    const contentWidth = contentRef.current.scrollWidth;
    return -(contentWidth - containerWidth);
  }, []);

  const constrainPosition = React.useCallback((newPosition) => {
    const maxPosition = getMaxPosition();
    if (newPosition > 0) return 0;
    if (newPosition < maxPosition) return maxPosition;
    return newPosition;
  }, [getMaxPosition]);

  const handleMouseDown = React.useCallback((e) => {
    if (!showArrows) return;

    if (!scrollRef.current) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      position: position
    };
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  }, [position, showArrows]);

  const handleMouseMove = React.useCallback((e) => {

    if (!showArrows) return;

    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const walk = e.clientX - dragStartRef.current.x;
    const newPosition = constrainPosition(dragStartRef.current.position + walk);
    setPosition(newPosition);
  }, [isDragging, constrainPosition, showArrows]);

  const handleMouseUp = React.useCallback(() => {

    if (!showArrows) return;

    setIsDragging(false);
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = '';
    }
  }, [showArrows]);

  const handleTouchStart = React.useCallback((e) => {
    if (!e || !e.touches || !e.touches.length) return;
    const clientX = e.touches[0].clientX;
    if (!clientX) return;
    touchDragRef.current = {
      x: clientX,
      position: position
    };
  }, [position]);

  const handleTouchMove = React.useCallback((e) => {
    if (!e || !e.touches || !e.touches.length) return;
    const clientX = e.touches[0].clientX;
    if (!clientX) return;

    if (touchDragRef.current.x === -999) {
      touchDragRef.current = {
        x: clientX,
        position: position
      };
      return;
    }

    const walk = touchDragRef.current.x - clientX;
    const newPosition = constrainPosition(touchDragRef.current.position - walk);
    setPosition(newPosition);
  }, [position, constrainPosition]);

  const handleTouchEnd = React.useCallback(() => {
    touchDragRef.current = { x: -999, position: 0 };
  }, []);

  React.useEffect(() => {
    checkScrollability();
    
    const handleResize = () => {
      // Constrain position after resize
      const constrainedPosition = constrainPosition(position);
      if (constrainedPosition !== position) {
        setPosition(constrainedPosition);
      }
      checkScrollability();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [checkScrollability, position, constrainPosition]);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    // Check after content loads
    const timer = setTimeout(checkScrollability, 100);
    return () => clearTimeout(timer);
  }, [children, checkScrollability]);

  React.useEffect(() => {
    checkScrollability();
  }, [position, checkScrollability]);

  const scrollLeft = () => {
    const newPosition = constrainPosition(position + 150);
    setPosition(newPosition);
  };

  const scrollRight = () => {
    const newPosition = constrainPosition(position - 150);
    setPosition(newPosition);
  };

  return (
    <Container>
      {showArrows && canScrollLeft && (
        <ArrowContainer left onClick={scrollLeft} padding={'0 7px 0 0'}>
          <Arrow direction="left" active={true} size="12px" />
        </ArrowContainer>
      )}
      {showArrows && canScrollRight && (
        <ArrowContainer right onClick={scrollRight} padding={'0 0 0 7px'}>
          <Arrow active={true} size="12px" />
        </ArrowContainer>
      )}
      <ScrollContainer 
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}>
        <ContentWrapper 
          ref={contentRef}
          position={position}
          isDragging={isDragging}
          style={{ left: position, display: showArrows ? 'inline-block' : 'flex', justifyContent: !showArrows ? 'center' : undefined }}>
          {children}
        </ContentWrapper>
      </ScrollContainer>
    </Container>
  );
};

